import {
  IllustrationRequestPost,
  JourneyLabelSnakeCase,
  OffshoreBonds
} from '@interfaces/OffshoreBonds.interface';
import BaseApiClient from '../BaseApiClient';

import axios from 'axios';
import { DataValues } from '@interfaces/OffshoreApplication';
import { ListDataParams, ListDataResponse } from '../types';
import { DocuSignEmbeddedView } from '@interfaces/FlightPlan';
import { InvestmentOption } from '../../components/page-components/offshore-application-3-steps/common/09-investment-options';
type VerifyIllustrationNumberResponse = {
  id: number;
};
export type ApplicationTypeValue =
  | 'Individual'
  | 'Joint'
  | 'Trust'
  | 'Corporate';

export type ApplicationType = {
  name: ApplicationTypeValue;
  id: number;
  description: string;
  display: string;
};

axios.defaults.withCredentials = true;

export class ApiOffshore extends BaseApiClient {
  async offshoreClientAccountsList(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<any>> {
    if (searchTerm) {
      if (!listData.filters) listData.filters = {};
      listData.filters.client_account_number = searchTerm;
    }
    const { data } = await this.client.getList(
      '/offshore-client-accounts',
      listData
    );

    return {
      items: data.data,
      pagination: {
        page: data.meta.current_page,
        perPage: data.meta.per_page,
        total: data.meta.total
      }
    };
  }

  async verifyIllustrationNumber(
    illustrationNumber: string
  ): Promise<VerifyIllustrationNumberResponse> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/wealth/wealth-user/illustrations/${illustrationNumber}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getOffshoreCompanyName(): Promise<{ name: string }> {
    return new Promise((resolve, reject) => {
      this.client.get('/entities/wealth').then((res) => resolve(res.data.data));
    });
  }

  async getInvestmentOptions(): Promise<InvestmentOption[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/investment-options')
        .then((res) => resolve(res?.data?.data))
        .catch(reject);
    });
  }

  async getApplicationTypes(): Promise<ApplicationType[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/wealth-policy-types')
        .then((res) => {
          if (res.data.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  getEmbeddedView({
    id
  }: {
    id: number;
    recipient_name: string;
    recipient_email: string;
  }): Promise<DocuSignEmbeddedView> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/docu-sign/embedded-view?director_id=${id}`)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  }

  async StartJourney({
    wealth_product_id,
    wealth_policy_type_id,
    illustration_id
  }: {
    wealth_product_id: string | number;
    wealth_policy_type_id: number;
    illustration_id: string;
  }): Promise<OffshoreBonds> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/wealth/wealth-user/onboarding', {
          wealth_product_id,
          wealth_policy_type_id, // joint, individual 1
          illustration_id
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async sendKycRequest(wealthUserId: number): Promise<OffshoreBonds> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/wealth/send-kyc-request/${wealthUserId}`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  async SendDocumentsData({
    wealth_product_id,
    documentsData
  }: {
    wealth_product_id: string | number;
    documentsData: {
      wealth_users?: Array<{
        individual_id: number;
        documents: File[];
      }>;
    };
  }): Promise<OffshoreBonds> {
    // Create FormData for file upload
    const formData = new FormData();

    // Add wealth_users documents
    if (documentsData.wealth_users) {
      documentsData.wealth_users.forEach((user, index) => {
        formData.append(
          `wealth_users[${index}][individual_id]`,
          user.individual_id.toString()
        );
        user.documents.forEach((file, fileIndex) => {
          formData.append(
            `wealth_users[${index}][documents][${fileIndex}]`,
            file
          );
        });
      });
    }
    return new Promise((resolve, reject) => {
      this.client
        .post(
          `/wealth/wealth-user/onboarding/${wealth_product_id}/documents`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async SendFormDocumentsData({
    wealth_product_id,
    data
  }: {
    wealth_product_id: string | number;
    data: {
      step: string;
      document: File;
      individual_id: number;
      wealthable_id?: number;
      wealth_individual_id?: number;
    };
  }): Promise<OffshoreBonds> {
    // Create FormData for file upload
    const formData = new FormData();

    // Add wealth_users documents
    formData.append('step', data.step);
    formData.append('document', data.document);
    if (data.individual_id) {
      formData.append('individual_id', data.individual_id.toString());
    }
    if (data.wealth_individual_id) {
      formData.append(
        'wealth_individual_id',
        data.wealth_individual_id.toString()
      );
    }
    if (data.wealthable_id) {
      formData.append('wealthable_id', data.wealthable_id.toString());
    }

    return new Promise((resolve, reject) => {
      this.client
        .post(
          `/wealth/wealth-user/onboarding/${wealth_product_id}/documents`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
  async NavigateAndSaveUserJourney(
    dataId: string,
    navigation: 'next' | 'previous' | 'stop' | 'direct',
    payload: DataValues,
    target_step?: JourneyLabelSnakeCase
  ): Promise<OffshoreBonds> {
    return new Promise((resolve, reject) => {
      this.client
        .patch(`/wealth/wealth-user/onboarding/${dataId}`, {
          navigation,
          target_step: target_step,
          ...{
            ...payload
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
    // Return the mock response directly for testing
    // return this.localJourneyState;
  }

  async SubmitApplication(
    dataId: string,
    navigation: 'next' | 'previous' | 'stop' | 'direct',
    payload: DataValues,
    target_step?: JourneyLabelSnakeCase
  ): Promise<OffshoreBonds> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/wealth/wealth-user/onboarding/${dataId}/apply`, {
          navigation,
          target_step: target_step,
          ...{
            ...payload
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        });
    });
  }

  async ValidateUserJourney(
    dataId: string,
    navigation: 'next' | 'previous' | 'stop' | 'direct',
    payload: DataValues,
    target_step?: JourneyLabelSnakeCase
  ): Promise<OffshoreBonds> {
    return new Promise((resolve, reject) => {
      this.client
        .patch(`/wealth/wealth-user/onboarding/${dataId}/validate`, {
          navigation,
          target_step: target_step,
          ...{
            ...payload
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
    // Return the mock response directly for testing
    // return this.localJourneyState;
  }

  async SaveAndValidateUserJourney() {
    return new Promise((resolve, reject) => {
      this.client
        .post('/wealth/wealth-user')
        .then((res) => {
          if (res.status === 204) {
            resolve(res.status);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async requestIllustration(fields: IllustrationRequestPost) {
    return this.client.post('/products/wealth/illustrations', fields);
  }
  async sendEmailDocusign(wealthPolicyId: string | undefined) {
    return this.client.post(`/wealth/docu-sign/send-email/${wealthPolicyId}`);
  }
  async getDocusignStatus(wealthPolicyId: string | undefined) {
    return this.client.get(`/wealth/agreement-status/${wealthPolicyId}`);
  }
  async deleteDocument(
    wealth_product_id: string | number,
    document_id: number
  ): Promise<OffshoreBonds> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(
          `/wealth/wealth-user/onboarding/${wealth_product_id}/documents/${document_id}`
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { offshoreQueryKeys } from './query-keys';
export type { OffshoreClientAccountsListQueryParams } from './query-keys';
export {
  useOffshoreApplicationTypesQuery,
  useOffshoreClientAccountsListQuery,
  useOffshoreCompanyNameQuery,
  useOffshoreDocusignStatusQuery,
  useOffshoreEmbeddedViewQuery,
  useOffshoreInvestmentOptionsQuery,
  useOffshoreVerifyIllustrationNumberQuery
} from './use-offshore-queries';
export {
  useOffshoreDeleteDocumentMutation,
  useOffshoreNavigateAndSaveUserJourneyMutation,
  useOffshoreRequestIllustrationMutation,
  useOffshoreSaveAndValidateUserJourneyMutation,
  useOffshoreSendDocumentsDataMutation,
  useOffshoreSendEmailDocusignMutation,
  useOffshoreSendFormDocumentsDataMutation,
  useOffshoreSendKycRequestMutation,
  useOffshoreStartJourneyMutation,
  useOffshoreSubmitApplicationMutation,
  useOffshoreValidateUserJourneyMutation
} from './use-offshore-mutations';
export type {
  OffshoreDeleteDocumentVariables,
  OffshoreNavigateAndSaveUserJourneyVariables,
  OffshoreSendDocumentsDataVariables,
  OffshoreSendFormDocumentsDataVariables,
  OffshoreStartJourneyVariables,
  OffshoreSubmitApplicationVariables,
  OffshoreValidateUserJourneyVariables
} from './use-offshore-mutations';
